import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';

export interface CashierCountRequest {
  id: string;
  organization_id: string;
  location_id: string;
  cashier_name: string;
  cashier_user_id: string | null;
  request_token: string;
  approval_token: string;
  status: 'pending' | 'approved' | 'denied' | 'expired';
  report_email: string | null;
  notes: string | null;
  cashier_count_id: string | null;
  expires_at: string;
  approved_at: string | null;
  denied_at: string | null;
  denied_reason: string | null;
  created_at: string;
  location_name?: string;
}

export function useCashierCountRequests() {
  const queryClient = useQueryClient();
  const { currentLocation, locationScope } = useLocationStore();
  const locationId = currentLocation?.id || null;

  const requestsQuery = useQuery({
    queryKey: ['cashier-count-requests', locationId, locationScope],
    queryFn: async () => {
      let query = supabase
        .from('cashier_count_requests')
        .select(`
          *,
          location:locations(name)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (locationScope === 'single' && locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((r: any) => ({
        ...r,
        location_name: r.location?.name,
      })) as CashierCountRequest[];
    },
  });

  const approveRequest = useMutation({
    mutationFn: async (approvalToken: string) => {
      const { data, error } = await supabase.rpc('approve_count_request', {
        p_approval_token: approvalToken,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-requests'] });
      queryClient.invalidateQueries({ queryKey: ['cashier-counts'] });
    },
  });

  const denyRequest = useMutation({
    mutationFn: async ({ approvalToken, reason }: { approvalToken: string; reason?: string }) => {
      const { data, error } = await supabase.rpc('deny_count_request', {
        p_approval_token: approvalToken,
        p_reason: reason || null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-requests'] });
    },
  });

  return {
    requests: requestsQuery.data || [],
    isLoading: requestsQuery.isLoading,
    error: requestsQuery.error,
    approveRequest,
    denyRequest,
    refetch: requestsQuery.refetch,
  };
}
